import { useEffect, useState } from "react";
import type { DocHeading } from "./markdownHeadings";

type DocsTocProps = {
    headings: DocHeading[];
};

/**
 * On-this-page navigation. The active entry is tracked with an
 * IntersectionObserver rooted so that a heading counts as current once it
 * reaches the top quarter of the viewport, which matches how a reader
 * perceives "where am I" better than raw scroll offsets.
 */
export default function DocsToc({ headings }: DocsTocProps) {
    const [activeId, setActiveId] = useState<string | null>(null);

    useEffect(() => {
        if (headings.length === 0) return;

        const elements = headings.map((heading) => document.getElementById(heading.id)).filter((el): el is HTMLElement => el !== null);
        if (elements.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible.length > 0) {
                    setActiveId(visible[0].target.id);
                }
            },
            { rootMargin: "-96px 0px -75% 0px", threshold: 0 },
        );

        elements.forEach((element) => observer.observe(element));
        return () => observer.disconnect();
    }, [headings]);

    if (headings.length === 0) return null;

    return (
        <nav aria-label="On this page" className="text-sm">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">On this page</div>
            <ul className="space-y-1 border-l border-slate-800">
                {headings.map((heading) => {
                    const isActive = activeId === heading.id;
                    return (
                        <li key={heading.id}>
                            <a
                                href={`#${heading.id}`}
                                className={`block border-l-2 py-1 transition-colors ${heading.depth === 3 ? "pl-6 text-xs" : "pl-3"} ${
                                    isActive
                                        ? "-ml-px border-orange-500 text-orange-300"
                                        : "-ml-px border-transparent text-slate-400 hover:border-slate-600 hover:text-slate-200"
                                }`}
                            >
                                {heading.text}
                            </a>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
}
